import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { categories, comics, getHotComics, getNewComics, getLatestComics, formatNumber } from '@/mocks/comics';

const bannerComics = [
  {
    id: 'comic-1',
    title: '我的野蛮王妃',
    author: '朴美珍',
    description: '现代女孩穿越古代朝鲜，与冷酷王子展开跌宕起伏的爱情故事。宫廷权谋与浪漫爱情交织！',
    coverImage: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20romance%20banner%20art%2C%20beautiful%20princess%20in%20traditional%20hanbok%20dress%20in%20ancient%20palace%20garden%2C%20dreamy%20atmosphere%20with%20cherry%20blossoms%2C%20dramatic%20manga%20illustration%20style%2C%20warm%20golden%20hour%20lighting%2C%20cinematic%20composition%2C%20soft%20romantic%20vibe&width=800&height=450&seq=home-hero-01&orientation=landscape',
    tag: '人气TOP1',
  },
  {
    id: 'comic-10',
    title: '命运之轮',
    author: '宋惠乔',
    description: '两个被命运捉弄的人在时间洪流中反复相遇又错过，跨越时空的虐心爱情巨作。',
    coverImage: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20drama%20banner%20art%2C%20two%20silhouettes%20under%20starry%20night%20sky%20with%20cherry%20blossom%20petals%2C%20emotional%20romantic%20manga%20illustration%2C%20mystical%20atmosphere%20with%20golden%20light%20particles%2C%20cinematic%20wide%20composition&width=800&height=450&seq=home-hero-02&orientation=landscape',
    tag: '催泪神作',
  },
  {
    id: 'comic-7',
    title: '王的男人',
    author: '赵恩静',
    description: '朝鲜时代平民少年入宫成为王的近侍，波诡云谲的宫廷中谱写壮烈历史传奇。',
    coverImage: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20historical%20banner%20art%2C%20handsome%20warrior%20in%20traditional%20Korean%20armor%20standing%20in%20ancient%20palace%20courtyard%2C%20moonlight%20scene%2C%20epic%20historical%20manga%20illustration%2C%20dramatic%20lighting%2C%20cinematic%20composition&width=800&height=450&seq=home-hero-03&orientation=landscape',
    tag: '历史巨制',
  },
];

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const hotComics = getHotComics(8);
  const newComics = getNewComics(8);
  const latestComics = getLatestComics(12);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % bannerComics.length);
  }, []);

  const prevBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev - 1 + bannerComics.length) % bannerComics.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩国漫画网',
    url: 'https://www.koreanmanhua.com/',
    description: '韩国漫画网为您提供免费高质量韩漫大全，在线阅读便捷流畅，高清画质与丰富内容满足您的阅读需求。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.koreanmanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOHead
        title="首页"
        description="韩国漫画网为您提供免费高质量韩漫大全，在线阅读便捷流畅，高清画质与丰富内容满足您的阅读需求，随时随地畅享韩漫阅读乐趣，热门新番实时更新，各类题材应有尽有。"
        keywords="韩国漫画网,免费漫画,高质量韩漫,在线阅读,韩漫大全,免费韩漫,高清漫画,韩漫在线看"
        canonicalUrl="/"
        schema={schema}
      />

      <main>
        <Navbar />

        {/* Hero Banner */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden" aria-label="推荐漫画轮播">
          {bannerComics.map((comic, index) => (
            <div
              key={comic.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={comic.coverImage}
                alt={comic.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-center">
                <div className="w-full px-4 md:px-6">
                  <div className="max-w-3xl">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 mb-3">
                      {comic.tag}
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold text-background-50 font-heading mb-3 leading-tight">
                      {comic.title}
                    </h2>
                    <p className="text-sm md:text-base text-background-200 mb-2">作者：{comic.author}</p>
                    <p className="text-sm md:text-base text-background-200/80 mb-6 max-w-lg leading-relaxed line-clamp-2">
                      {comic.description}
                    </p>
                    <Link
                      to={`/comic/${comic.id}`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                    >
                      立即阅读
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={prevBanner}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center hover:bg-background-50/40 transition-all cursor-pointer"
            aria-label="上一张"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-background-50"></i>
            </div>
          </button>
          <button
            onClick={nextBanner}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center hover:bg-background-50/40 transition-all cursor-pointer"
            aria-label="下一张"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-background-50"></i>
            </div>
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
            {bannerComics.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  index === currentBanner ? 'bg-primary-500 w-6' : 'bg-background-50/50 hover:bg-background-50/80'
                }`}
                aria-label={`切换到第${index + 1}张`}
              ></button>
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-50" aria-label="漫画分类导航">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">漫画分类</h2>
              <Link to="/category/romance" className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap">
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-4 md:p-5 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-3 group-hover:bg-primary-500 transition-colors">
                    <i className={`${cat.icon} text-lg md:text-xl text-primary-500 group-hover:text-background-50 transition-colors`}></i>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-1 font-heading">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 line-clamp-2">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics */}
        <section className="py-10 md:py-14 bg-background-50" aria-label="热门推荐">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">热门推荐</h2>
                <p className="text-sm text-foreground-500 mt-1">最受读者欢迎的韩漫作品</p>
              </div>
              <Link to="/category/romance" className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap">
                更多热门
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {hotComics.map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩国漫画网热门韩漫推荐`}
                      title={`${comic.title} - 免费高质量韩漫`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-fire-line text-[10px]"></i>
                        </div>
                        热门
                      </span>
                    </div>
                    {index < 3 && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-md bg-accent-500 flex items-center justify-center">
                        <span className="text-xs font-bold text-background-50">{index + 1}</span>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <div className="flex items-center gap-3 text-xs text-background-200">
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-eye-line text-[10px]"></i>
                          </div>
                          {formatNumber(comic.views)}
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-heart-line text-[10px]"></i>
                          </div>
                          {formatNumber(comic.likes)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-accent-500 font-medium flex items-center gap-0.5">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-[10px]"></i>
                      </div>
                      {comic.rating}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'}`}>
                      {comic.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="py-10 md:py-14 bg-background-100" aria-label="新番上架">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                  <a href="#new-arrivals" className="cursor-pointer">新番上架</a>
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最新上架的韩漫作品</p>
              </div>
              <Link to="/category/romance" className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap">
                更多新番
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {newComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩国漫画网最新韩漫`}
                      title={`${comic.title} - 新番免费韩漫`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-sparkling-line text-[10px]"></i>
                        </div>
                        NEW
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {comic.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700">{tag}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 bg-background-50" aria-label="最近更新">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                  <a href="#latest-updates" className="cursor-pointer">最近更新</a>
                </h2>
                <p className="text-sm text-foreground-500 mt-1">热门作品最新章节及时更新</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {latestComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="flex items-center gap-4 p-3 md:p-4 rounded-xl bg-background-100 hover:bg-primary-50/50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden shrink-0">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩国漫画网更新`}
                      title={`${comic.title} - 韩漫更新时间`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-foreground-400">更新至 {comic.chapters.length} 话</span>
                      <span className="text-xs text-accent-500 font-medium flex items-center gap-0.5">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-[10px]"></i>
                        </div>
                        {comic.rating}
                      </span>
                    </div>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats CTA */}
        <section className="py-14 md:py-20 bg-primary-500" aria-label="平台数据">
          <div className="w-full px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-3">加入千万读者的韩漫世界</h2>
            <p className="text-background-200 text-sm md:text-base mb-10 max-w-xl mx-auto">
              每天更新，海量免费韩漫，随时随地享受阅读乐趣
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {[
                { value: '1000+', label: '漫画作品' },
                { value: '50000+', label: '漫画章节' },
                { value: '1000万+', label: '月活跃读者' },
                { value: '10万+', label: '日更新量' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">{stat.value}</div>
                  <div className="text-sm text-background-200/80 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 bg-background-50" aria-label="常见问题">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
              <a href="#faq" className="cursor-pointer">常见问题</a>
            </h2>
            <div className="space-y-3">
              {[
                { q: '韩国漫画网的漫画都是免费的吗？', a: '是的！韩国漫画网所有漫画资源均可免费在线阅读，无需付费订阅。我们致力于为漫画爱好者提供高质量的免费阅读体验。' },
                { q: '网站支持哪些设备阅读？', a: '韩国漫画网支持电脑、平板和手机等多种设备访问。我们的网页采用响应式设计，在不同屏幕尺寸下都能获得良好的阅读体验。' },
                { q: '更新频率是怎样的？', a: '我们每天都会更新漫画内容，热门作品基本与韩国同步更新。您可以随时访问网站查看最新章节！' },
                { q: '如何找到我想看的漫画？', a: '您可以通过首页的分类导航按题材筛选漫画，也可以使用顶部的搜索功能直接搜索漫画名称。我们还提供了热门推荐和新番上架等栏目帮您发现好作品。' },
              ].map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-950 pr-4">{faq.q}</span>
                    <div className="w-5 h-5 shrink-0 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}